import { __awaiter } from "tslib";
import { TedivoForm, translateTedivoForm } from "@tedivo/tedivo-form";
import { IntegratedDialog, IntegratedDialogError } from "@tedivo/tedivo-ui";
import { createFormFields, createFormValidator } from "./importNsdFields";
import { getFilenameParts, sanitizeString } from "@tedivo/tedivo-pure-helpers";
import { getTranslation, i18nReactive } from "../../../app/i18/i18tn";
import { I18nComponentRegisterer } from "@tedivo/tedivo-i18";
import { ImoFoundResultEnum } from "../import-staf/ImoFoundResultEnum";
import LenUnitsEnum from "../../../app/enums/LenUnitsEnum";
import Services from "../../../app/services";
import { createFoundImoDialog } from "../import-staf/createFoundImoDialog";
import { createNotFoundImoDialog } from "../import-staf/createNotFoundImoDialog";
import { focusAndSelect } from "../import-staf/focusAndSelect";
import globalStore from "../../../app/stores/globalStore";
import globalUnits from "../../../app/units/globalUnits";
import goSquared from "../../../app/tracking/goSquared";
import { routeFns } from "../../../app/router/routes";
import router from "../../../app/router";
import securityModule from "../../../app/security/SecurityModule";
import { setAppTitle } from "../../../app/setAppTitle";
import { splitNameForMainNames } from "../../../app/services/import-staf/stafFileConvertToOVD";
import topMessageElement from "../../layout/top-tools/getTopMessageElement";
/**
 * Import a Navis .nsd file as a new OVD.
 *
 * Modelled on the STAF import, with one structural difference: an .nsd is binary
 * and is parsed by the converter Lambda, so nothing about the file can be
 * inspected here. The form therefore always offers LBP/LOA instead of deciding
 * whether they are needed, and the conversion itself happens in the view-json
 * component once this form has been submitted.
 */
export class TVDImportNsdComponent extends HTMLElement {
    constructor() {
        super();
        this.dialogError = new IntegratedDialogError(this, false, getTranslation("general:common.close"));
        this.submitButton = null;
        this.ownFiles = {
            lastUpdated: 0,
            list: [],
        };
        this.shipFieldsState = {
            name: "",
            shipName: "",
            shipClass: "",
            vmdShipNameLatest: undefined,
        };
        this.readyToSubmit = false;
        /**
         * The selected NSD file.
         *
         * Held on the component because `showOtherFields` calls `tedivoForm.setFields()`,
         * which rebuilds every control. A file input's value cannot be restored
         * programmatically (`FileElement.value` setter is a no-op), so the File would
         * otherwise be lost between selection and submit.
         */
        this.selectedFile = undefined;
        this.internalMatch = undefined;
        this.imoApiState = ImoFoundResultEnum.INITIALIZED;
        this.onSubmitDataForm = (values) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            globalStore.touchLastUserInteraction();
            if (!this.readyToSubmit)
                return;
            if (this.submitButton) {
                this.submitButton.loading = true;
                this.submitButton.disabled = true;
            }
            const file = this.selectedFile;
            if (file === undefined) {
                this.showError(undefined, "general:common.attention", "errors:notValidNsdFile");
                if (this.submitButton) {
                    this.submitButton.loading = false;
                    this.submitButton.disabled = false;
                }
                return;
            }
            const filenameParts = getFilenameParts(file.name);
            const name = sanitizeString(filenameParts.name);
            const viewState = {
                file,
                filenameParts,
                source: "nsdFile",
                vmdShipNameLatest: this.shipFieldsState.vmdShipNameLatest,
                formFields: {
                    lpp: Number((_a = (values.lpp || this.shipFieldsState.lppFromApi)) !== null && _a !== void 0 ? _a : 0),
                    loa: Number((_b = (values.loa || this.shipFieldsState.loaFromApi)) !== null && _b !== void 0 ? _b : 0),
                    shipClass: values.shipClass || "",
                    shipName: values.shipName || "",
                    lineOperator: values.lineOperator || "",
                    imoCode: values.imoCode || "",
                    callSign: values.callSign,
                    yearBuilt: values.yearBuilt,
                },
            };
            yield this.getOwnFiles();
            if (this.ownFiles.list.some(({ name }) => name === values.shipName)) {
                const modal = new IntegratedDialog({
                    parentNode: this,
                    preventDefaultClose: true,
                    preventClose: true,
                    buttonsAttrs: [
                        {
                            label: getTranslation("general:common.saveAnyway"),
                            value: "saveAnyway",
                            variant: "danger",
                            icon: "exclamation-triangle",
                            type: "submit",
                        },
                        {
                            label: getTranslation("general:common.cancel"),
                            value: "cancel",
                            icon: "x-lg",
                            type: "button",
                            autofocus: true,
                        },
                    ],
                    removeDialogWhenHidden: true,
                });
                modal.show(getTranslation("general:common.attention"), getTranslation("errors:fileWithSameNameExists"));
                modal.onButtonClicked = (btnSelected) => {
                    if (btnSelected === "saveAnyway") {
                        goSquared.addEvent("Import-NSD - Exec");
                        router.navigate(routeFns.ovdEdit(name), viewState);
                    }
                };
            }
            else {
                goSquared.addEvent("Import-NSD - Exec");
                router.navigate(routeFns.ovdEdit(name), viewState);
            }
            if (this.submitButton) {
                this.submitButton.loading = false;
                this.submitButton.disabled = false;
            }
        });
        this.showError = (e, errorTlt = "errors:errorHasOcurred", errorStr = "errors:notValidNsdFile") => {
            this.dialogError.show(getTranslation(errorTlt), getTranslation(errorStr));
            if (e !== undefined)
                console.error(e);
        };
        this.i18nCR = new I18nComponentRegisterer(i18nReactive);
        setAppTitle(getTranslation("menu:fileImportNsd"));
    }
    connectedCallback() {
        // The converter Lambda rejects nsd2ovd for organizations without the flag,
        // so the page is pointless (and misleading) for them.
        if (securityModule.planIsReadOnly || !securityModule.currentOrganizationNsdToOvdAllowed) {
            router.navigate(routeFns.myCloud());
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const me = this;
        goSquared.trackPage("Import-NSD");
        goSquared.addEvent("Import-NSD - Show page");
        const wrapper = document.createElement("div"), h1 = document.createElement("h1");
        const box = document.createElement("div");
        box.className = "sections-oss-card";
        const submitButton = document.createElement("sl-button");
        submitButton.type = "submit";
        submitButton.innerHTML = getTranslation("general:common.submit");
        submitButton.variant = "primary";
        submitButton.disabled = true;
        this.submitButton = submitButton;
        const nextButton = document.createElement("sl-button");
        nextButton.type = "button";
        nextButton.innerHTML = getTranslation("general:common.next");
        nextButton.outline = true;
        nextButton.variant = "primary";
        const tedivoForm = new TedivoForm({
            fields: createFormFields({ imoCode: "" }),
            onSubmit: this.onSubmitDataForm,
            formValidator: createFormValidator(),
            submitButton: submitButton,
        });
        const loadingBlocker = document.createElement("loading-blocker-element");
        this.appendChild(loadingBlocker);
        translateTedivoForm({
            tedivoForm,
            i18nCR: this.i18nCR,
        });
        tedivoForm.onDataChange = (values, name) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            globalStore.touchLastUserInteraction();
            switch (name) {
                case "fileInput": {
                    const fileInput = values.fileInput;
                    if (fileInput === undefined)
                        return;
                    this.selectedFile = fileInput;
                    const fnameParts = getFilenameParts(fileInput.name);
                    this.shipFieldsState.name = fnameParts.filename;
                    // The converter reads the vessel identity from the filename, never from
                    // the names embedded in the .nsd, so an extension-less name is rejected
                    // here rather than producing a nameless vessel later on.
                    if (fnameParts.name === "") {
                        this.showError(undefined, "general:common.attention", "errors:fileNameInvalid");
                        tedivoForm.getFormControlsByName().fileInput.field.reset();
                        this.selectedFile = undefined;
                        return;
                    }
                    // A file picked after the IMO was confirmed must refresh the fields
                    // derived from its name; this is a no-op before that point.
                    showOtherFields(this.shipFieldsState, this.i18nCR, this.imoApiState);
                    if (this.internalMatch) {
                        const match = this.internalMatch;
                        tedivoForm
                            .setValue("shipName", match.SHIPNAME)
                            .setValue("callSign", match.CALLSIGN || "N/A")
                            .setValue("yearBuilt", Number((_a = match.BUILT) !== null && _a !== void 0 ? _a : 0));
                    }
                    break;
                }
                case "imoCode": {
                    if (!values.imoCode)
                        break;
                    loadingBlocker.show();
                    const [nameInfo, searchResults] = yield Promise.all([
                        Services.vesselsMasterData.searchVesselByImoCode(values.imoCode),
                        Services.vesselSearch.search(`'${values.imoCode}'`, ["imoCode"]),
                    ]);
                    const tvlResults = ((_b = searchResults === null || searchResults === void 0 ? void 0 : searchResults.data) === null || _b === void 0 ? void 0 : _b.filter((r) => r.item.organizationId !== securityModule.currentOrganizationId)) || [];
                    loadingBlocker.hide();
                    if (((_c = nameInfo === null || nameInfo === void 0 ? void 0 : nameInfo.data) === null || _c === void 0 ? void 0 : _c.source) === "error") {
                        if (tedivoForm.execValidation(["imoCode", "fileInput"]).success) {
                            this.imoApiState = ImoFoundResultEnum.NOT_FOUND_CONTINUE;
                            showOtherFields(this.shipFieldsState, this.i18nCR, this.imoApiState);
                        }
                    }
                    const match = (_d = nameInfo === null || nameInfo === void 0 ? void 0 : nameInfo.data) === null || _d === void 0 ? void 0 : _d.match;
                    this.internalMatch = match;
                    if (!match) {
                        createNotFoundImoDialog({
                            parentNode: this,
                            imoCode: values.imoCode || "",
                            onButtonClicked: (btnSelected) => {
                                switch (btnSelected) {
                                    case "continue":
                                        this.imoApiState = ImoFoundResultEnum.NOT_FOUND_CONTINUE;
                                        break;
                                    case "fix":
                                        this.imoApiState = ImoFoundResultEnum.NOT_FOUND_FIX;
                                        setTimeout(() => {
                                            focusAndSelect(tedivoForm.getFormControlsByName().imoCode.field);
                                        }, 150);
                                        break;
                                }
                                if (tedivoForm.execValidation(["imoCode", "fileInput"]).success) {
                                    showOtherFields(this.shipFieldsState, this.i18nCR, this.imoApiState);
                                }
                            },
                        });
                    }
                    else {
                        createFoundImoDialog({
                            parentNode: this,
                            imoCode: values.imoCode || "",
                            btnSeeTvlSearchResults: tvlResults.length > 0,
                            lastFetched: String(match.modifiedAt),
                            shipName: match.SHIPNAME,
                            onButtonClicked: (btnSelected) => {
                                var _a, _b, _c;
                                switch (btnSelected) {
                                    case "continue":
                                        this.imoApiState = ImoFoundResultEnum.FOUND_CONTINUE;
                                        this.shipFieldsState.shipName = match.SHIPNAME;
                                        this.shipFieldsState.vmdShipNameLatest = match.SHIPNAME;
                                        this.shipFieldsState.lppFromApi =
                                            Number(((_a = match.mtParticularsJson) === null || _a === void 0 ? void 0 : _a.LENGTH_B_W_PERPENDICULARS) || 0) * 1000;
                                        this.shipFieldsState.loaFromApi =
                                            Number(((_b = match.mtParticularsJson) === null || _b === void 0 ? void 0 : _b.LENGTH_OVERALL) || 0) * 1000;
                                        // Show
                                        if (tedivoForm.execValidation(["imoCode", "fileInput"]).success) {
                                            showOtherFields(this.shipFieldsState, this.i18nCR, this.imoApiState);
                                        }
                                        // Set fields
                                        tedivoForm
                                            .setValue("shipName", match.SHIPNAME)
                                            .setValue("callSign", match.CALLSIGN || "N/A")
                                            .setValue("yearBuilt", Number((_c = match.BUILT) !== null && _c !== void 0 ? _c : 0))
                                            .setValue("lpp", this.shipFieldsState.lppFromApi)
                                            .setValue("loa", this.shipFieldsState.loaFromApi);
                                        break;
                                    case "fix":
                                        this.imoApiState = ImoFoundResultEnum.FOUND_FIX_IMO;
                                        if (tedivoForm.execValidation(["imoCode", "fileInput"]).success) {
                                            showOtherFields(this.shipFieldsState, this.i18nCR, this.imoApiState);
                                        }
                                        focusAndSelect(tedivoForm.getFormControlsByName().imoCode.field);
                                        break;
                                    case "doubleCheck":
                                        this.imoApiState = ImoFoundResultEnum.FOUND_MANUAL_SET;
                                        loadingBlocker.show();
                                        Services.vesselsMasterData
                                            .searchVesselByImoCode(values.imoCode || "", true)
                                            .then((doucleCheckNameInfo) => {
                                            var _a;
                                            loadingBlocker.hide();
                                            const newMatch = (_a = doucleCheckNameInfo === null || doucleCheckNameInfo === void 0 ? void 0 : doucleCheckNameInfo.data) === null || _a === void 0 ? void 0 : _a.match;
                                            if (newMatch) {
                                                this.shipFieldsState.shipName = newMatch.SHIPNAME;
                                                this.shipFieldsState.vmdShipNameLatest = match.SHIPNAME;
                                                tedivoForm.setValue("shipName", newMatch.SHIPNAME);
                                            }
                                        })
                                            .catch(() => {
                                            loadingBlocker.hide();
                                        });
                                        break;
                                    case "seeSearchResults":
                                        router.navigate(routeFns.communityFiles(), {
                                            searchText: values.imoCode,
                                            fields: ["imoCode"],
                                            data: searchResults.data,
                                        });
                                        break;
                                }
                            },
                        });
                    }
                    break;
                }
            }
            function showOtherFields(shipFieldsState, i18nCR, imoApiState) {
                const { name, shipClass, shipName, lppFromApi, loaFromApi } = shipFieldsState;
                const doContinue = imoApiState === ImoFoundResultEnum.FOUND_CONTINUE ||
                    imoApiState === ImoFoundResultEnum.FOUND_MANUAL_SET ||
                    imoApiState === ImoFoundResultEnum.NOT_FOUND_CONTINUE;
                if (!doContinue)
                    return;
                me.readyToSubmit = true;
                const nameFields = splitNameForMainNames(name || "", shipClass || "");
                if (shipName)
                    nameFields.shipName = shipName;
                const newFields = createFormFields({
                    imoCode: values.imoCode || "",
                    fileLabel: name,
                    lppFromApi,
                    loaFromApi,
                    withNameFields: nameFields,
                });
                tedivoForm.setFields(newFields).setFormValidator(createFormValidator({
                    withNameFields: true,
                    // setFields() rebuilds the file control; the File itself lives on the component
                    fileAlreadyLoaded: me.selectedFile !== undefined,
                }));
                const divButtons = document.createElement("div");
                divButtons.className = "form-buttons";
                divButtons.appendChild(submitButton);
                tedivoForm.form.appendChild(divButtons);
                translateTedivoForm({
                    tedivoForm,
                    i18nCR,
                });
                submitButton.disabled = false;
            }
        });
        box.appendChild(tedivoForm.form);
        const divButtons = document.createElement("div");
        divButtons.className = "form-buttons";
        divButtons.appendChild(nextButton);
        tedivoForm.form.appendChild(divButtons);
        wrapper.appendChild(h1);
        wrapper.appendChild(box);
        this.appendChild(wrapper);
        // Translations
        const i18nCR = this.i18nCR;
        i18nCR.addConsumer(h1, "menu:fileImportNsd", "innerHTML");
        i18nCR.addConsumer(topMessageElement.element, "menu:fileImportNsd", "innerHTML");
        nextButton.addEventListener("click", () => {
            var _a;
            (_a = tedivoForm.onDataChange) === null || _a === void 0 ? void 0 : _a.call(tedivoForm, tedivoForm.getValues(), "imoCode");
        });
        document.documentElement.addEventListener("globalUnitsChanged", () => {
            const helpText = `enums:LenUnitsEnum.${LenUnitsEnum[globalUnits.lengthUnits.units]}`;
            const controls = tedivoForm.getFormControlsByName();
            if (controls.lpp)
                i18nCR.addConsumer(controls.lpp.field, helpText, "helpText");
            if (controls.loa)
                i18nCR.addConsumer(controls.loa.field, helpText, "helpText");
        });
    }
    disconnectedCallback() {
        this.i18nCR.disconnect();
    }
    getOwnFiles() {
        return __awaiter(this, void 0, void 0, function* () {
            const lastUpdated = this.ownFiles.lastUpdated;
            //Fetch if first-time or cache is > 5s
            if (lastUpdated === 0 || Date.now() - lastUpdated > 5000) {
                const data = yield Services.files.getAll();
                if (data.statusCode === 200 && data.data !== undefined) {
                    this.ownFiles.list = data.data;
                    this.ownFiles.lastUpdated = Date.now();
                }
            }
        });
    }
}
TVDImportNsdComponent.observedAttributes = [];
customElements.define("tvd-import-nsd-component", TVDImportNsdComponent);
